'use strict';

const { noop } = require('../../../mixins/commonjs/basicUtils');

/**
 * Base class for carousel "view-models". Defines the interface that derived classes must implement. It is expected that
 * wrapper carousels will be implemented that actually construct the model and pass it into the base carousel. The
 * `onMount` method should be called in the base carousel's `componentDidMount` lifecycle callback and `destroy` should
 * be called in the `componentWillUnmount` lifecycle callback. Derived classes should be aware that the `_el` property
 * will not be available the first time the style transformation methods are called so they should provide a default
 * style that does not depend on the el.
 * @class BaseModel
 */
class BaseModel {
    constructor({ onStateChange = noop } = {}) {
        Object.assign(this, {
            _el: null,
            _onStateChange: onStateChange,
            _state: {},
        });
    }

    /**
     * Pass in the DOM element to manage. This should be a raw DOM element and not a React or jQuery object. The reason
     * the element cannot be passed into the constructor is that initial construction is meant to take place outside of
     * the base carousel but the element is only accessible after construction, once the base carousel has been rendered
     * for the first time. It is possible the "view-model" doesn't need to do anything with the element in which case
     * the `_el` property can be ignored.
     * @param  {Object} el DOM element to manage.
     */
    onMount(el) {
        if (!el) {
            throw new Error('Must pass `el` to carousel model on mount.');
        }

        this._el = el;
        this._afterMount();
    }

    /**
     * Call the state change callback with the current state.
     * @private
     */
    _updateState() {
        this._onStateChange(this._state);
    }

    /**
     * Return the current state.
     * @return {[type]} [description]
     */
    getState() {
        return this._state;
    }

    /**
     * Do any cleanup necessary to avoid memory leaks, e.g. removing event listeners from the passed in element.
     * @abstract
     */
    destroy() {}

    /**
     * Modify the styles object for the carousel `ul`. This gets called before a re-render of the carousel.
     * @param  {Object} style Style object to mutate for the carousel `el`.
     * @abstract
     */
    transformListStyle(style) {} // eslint-disable-line no-unused-vars

    /**
     * Modify the styles object for the carousel item `li`s. This gets called before a re-render of the carousel.
     * @param  {Object} style Style object to mutate for each carousel item `li`.
     * @param  {Number} i The index of the item.
     * @abstract
     */
    transformItemStyle(style, i) {} // eslint-disable-line no-unused-vars

    /**
     * Custom logic after an element is passed in `onMount`, e.g. adding event listeners on the element.
     * @abstract
     * @private
     */
    _afterMount() {}
}

module.exports = BaseModel;



// WEBPACK FOOTER //
// ./assets/js/global/components/carousel/base/BaseModel.es.js